<script setup lang="ts">
    import type { PublicCenshareConfig } from '@/utils/CenshareClient/CenshareClient';
    import defaultImage from '@/assets/images/no-image-fachkreis.jpg';

    const {
        imageId,
        type,
        name = '',
        invalidId = false,
        ariaHidden = false,
        eager = false,
        zoom = false,
        customClass = ''
    } = defineProps<{
        imageId: number | null;
        type: 'productCard' | 'productGallery' | 'productThumbnail' | 'crossSelling';
        name?: string;
        invalidId?: boolean;
        ariaHidden?: boolean;
        eager?: boolean;
        zoom?: boolean;
        customClass?: string;
    }>();

    const publicCenshareConfig = useRuntimeConfig().public.censhare as PublicCenshareConfig;
    const censhareEndpoint = publicCenshareConfig.endpoint;

    const imageRef = ref<HTMLImageElement | null>(null);

    const MAX_IMAGE_WIDTH = 1440;
    const MIN_IMAGE_WIDTH = 80;

    const imageType = (type: string) => {
        switch (type) {
            case 'productCard':
                return 'product_card';
            case 'productGallery':
                return 'product_gallery';
            case 'productThumbnail':
                return 'product_thumbnail';
            case 'crossSelling':
                return 'cross_selling';
            default:
                return 'not-valid-image-type';
        }
    };

    function buildUrl(size: number): string {
        return `${censhareEndpoint}/entity/pictures/${imageId}/storage/fachkreis_${imageType(type)}_${size}x`;
    }

    // target size in px – used to generate widths
    const targetSizeMap: Record<typeof type, number> = {
        productCard: 260,
        productGallery: 480,
        productThumbnail: 80,
        crossSelling: 412
    };

    const sizeMap: Record<typeof type, string> = {
        productCard: '(min-width: 1020px) 600px, (min-width: 768px) 50vw, 100vw',
        productGallery: '(min-width: 1020px) 480px, (min-width: 768px) 50vw, 98vw',
        productThumbnail: '(min-width: 1020px) 80px, (min-width: 768px) 50vw, 98vw',
        crossSelling: '(min-width: 1020px) 600px, (min-width: 768px) 50vw, 98vw'
    };

    const sizes = sizeMap[type];
    const base = targetSizeMap[type];

    // generate multiples for high pixel density displays
    const scaleFactors = [1, 2, 3];

    const scaledWidths = scaleFactors.map((factor) => Math.round(base * factor)); // scale base size
    const uniqueWidths = Array.from(new Set(scaledWidths)); // remove duplicates
    const widths = uniqueWidths.filter((width) => width <= MAX_IMAGE_WIDTH && width >= MIN_IMAGE_WIDTH); // clamp width

    let srcset: string;
    if (imageId) {
        srcset = widths.map((w, index) => `${buildUrl(index + 1)} ${w}w`).join(',');
    }

    const hasError = ref();

    const onImageError = (event: Event) => {
        const img = event.target as HTMLImageElement;
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = defaultImage;
        hasError.value = true;
    };

    onMounted(() => {
        if (imageRef.value?.complete && imageRef.value.naturalWidth === 0) {
            imageRef.value.src = defaultImage;
        }
    });
</script>

<template>
    <img
        v-if="imageId && !invalidId && !zoom"
        ref="imageRef"
        :srcset="hasError ? undefined : srcset"
        :sizes="hasError ? undefined : sizes"
        :alt="name"
        :loading="eager ? undefined : 'lazy'"
        :fetchpriority="eager ? 'high' : undefined"
        decoding="async"
        :aria-hidden="ariaHidden ? ariaHidden : 'false'"
        :class="customClass"
        data-testid="censhare-image"
        @error="onImageError"
    />
    <ClientOnly v-else-if="imageId && !invalidId && zoom">
        <VueImageZoomer
            :click-zoom="true"
            :show-message="false"
            :zoom-amount="3"
            :regular="buildUrl(2)"
            :zoom="buildUrl(2)"
            :regular-webp="buildUrl(3)"
            :zoom-webp="buildUrl(4)"
            :alt="name"
            :img-class="customClass"
        />

        <template #fallback>
            <img
                v-if="imageId && !invalidId"
                ref="imageRef"
                :srcset="hasError ? undefined : srcset"
                :sizes="hasError ? undefined : sizes"
                :alt="name"
                :loading="eager ? undefined : 'lazy'"
                :fetchpriority="eager ? 'high' : undefined"
                decoding="async"
                :aria-hidden="ariaHidden ? ariaHidden : 'false'"
                :class="customClass"
                data-testid="censhare-image"
                @error="onImageError"
            />
            <img
                v-else
                :src="defaultImage"
                :class="customClass"
                alt="no-image"
            />
        </template>
    </ClientOnly>
    <img
        v-else
        :src="defaultImage"
        :img-class="customClass"
        data-testid="censhare-image"
        alt="no-image"
    />
</template>

<style lang="scss">
    @import '~/assets/scss/censhare/censhare-image.scss';
</style>
